import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ErrorNote,
  Field,
  Input,
  PageTitle,
  SaveState,
  Select,
  Spinner,
  Textarea,
} from '../components/ui.jsx';

const ICONS = ['sparkle', 'asterisk', 'star', 'burst'];

function Editor({ item, onSaved, onCancel }) {
  const [form, setForm] = useState({
    title: '',
    summary: '',
    description: '',
    icon: 'sparkle',
    order: 0,
    isFeatured: false,
    status: 'PUBLISHED',
    ...item,
  });
  const [state, setState] = useState('idle');
  const [error, setError] = useState(null);

  const isNew = !item?.id;

  async function save(event) {
    event.preventDefault();
    setState('saving');
    setError(null);

    const payload = {
      title: form.title,
      summary: form.summary,
      description: form.description || null,
      icon: form.icon,
      order: Number(form.order) || 0,
      isFeatured: Boolean(form.isFeatured),
      status: form.status,
    };

    try {
      if (isNew) await api.post('/api/admin/services', payload);
      else await api.patch(`/api/admin/services/${item.id}`, payload);
      setState('saved');
      onSaved();
    } catch (err) {
      setState('error');
      setError(err.message);
    }
  }

  return (
    <Card className="p-8">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-2xl">{isNew ? 'New service' : 'Edit service'}</h2>
        <SaveState state={state} />
      </div>

      <ErrorNote error={error} />

      <form onSubmit={save} className="space-y-6">
        <Field label="Title" htmlFor="title" required>
          <Input
            id="title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
          />
        </Field>

        <Field label="Summary" htmlFor="summary" required hint="One or two lines, shown on cards.">
          <Textarea
            id="summary"
            rows={3}
            value={form.summary}
            onChange={(e) => setForm({ ...form, summary: e.target.value })}
            required
          />
        </Field>

        <Field
          label="Meta"
          htmlFor="description"
          hint="Small print under the title, e.g. “7–8 posts / week”."
        >
          <Input
            id="description"
            value={form.description ?? ''}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </Field>

        <div className="grid items-end gap-5 md:grid-cols-4">
          <Field label="Mark" htmlFor="icon">
            <Select
              id="icon"
              value={form.icon}
              onChange={(e) => setForm({ ...form, icon: e.target.value })}
            >
              {ICONS.map((icon) => (
                <option key={icon} value={icon}>
                  {icon}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Order" htmlFor="order">
            <Input
              id="order"
              type="number"
              value={form.order}
              onChange={(e) => setForm({ ...form, order: e.target.value })}
            />
          </Field>

          <Field label="Status" htmlFor="status">
            <Select
              id="status"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <option value="PUBLISHED">Published</option>
              <option value="DRAFT">Draft</option>
              <option value="ARCHIVED">Archived</option>
            </Select>
          </Field>

          <Checkbox
            label="Featured"
            checked={Boolean(form.isFeatured)}
            onChange={(e) => setForm({ ...form, isFeatured: e.target.checked })}
          />
        </div>

        <div className="flex gap-3 border-t-3 border-ink pt-6">
          <Button type="submit" disabled={state === 'saving'}>
            Save service
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

export default function Services() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);

  function load() {
    setError(null);
    api
      .get('/api/admin/services')
      .then((data) => setItems(data.items))
      .catch((err) => setError(err.message));
  }

  useEffect(load, []);

  async function remove(item) {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await api.delete(`/api/admin/services/${item.id}`);
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  if (editing !== null) {
    return (
      <Editor
        item={editing}
        onSaved={() => {
          setEditing(null);
          load();
        }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <>
      <PageTitle
        title="Services"
        description="These drive the services page and the home page grid."
        action={<Button onClick={() => setEditing({})}>+ New service</Button>}
      />

      <ErrorNote error={error} onRetry={load} />

      {!items ? (
        <Spinner />
      ) : (
        <Card>
          <table className="w-full text-left text-sm">
            <thead className="border-b-3 border-ink">
              <tr>
                <th className="p-4 text-xs uppercase tracking-widest">#</th>
                <th className="p-4 text-xs uppercase tracking-widest">Title</th>
                <th className="p-4 text-xs uppercase tracking-widest">Mark</th>
                <th className="p-4 text-xs uppercase tracking-widest">Status</th>
                <th className="p-4 text-xs uppercase tracking-widest">Featured</th>
                <th className="p-4" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b border-line last:border-0">
                  <td className="p-4 text-muted">{item.order}</td>
                  <td className="p-4 font-semibold">{item.title}</td>
                  <td className="p-4 text-muted">{item.icon}</td>
                  <td className="p-4">
                    <Badge tone={item.status}>{item.status}</Badge>
                  </td>
                  <td className="p-4">{item.isFeatured ? 'Yes' : '—'}</td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" size="sm" onClick={() => setEditing(item)}>
                        Edit
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => remove(item)}>
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
